import { useEffect, useMemo, useState } from 'react';

import { ProductCard } from './ProductCard';
import { CATEGORIES, getProducts } from '../api/getProducts';

export function Shop({ products: productsProp }) {
  const [fetchedProducts, setFetchedProducts] = useState([]);
  const [loading, setLoading] = useState(!productsProp);
  const [loadError, setLoadError] = useState('');
  const [filter, setFilter] = useState('All');

  useEffect(() => {
    // If a products list was passed in directly, skip fetching entirely.
    if (productsProp) return;

    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const data = await getProducts();
        if (!cancelled) {
          setFetchedProducts(data);
          setLoadError('');
        }
      } catch (err) {
        if (!cancelled) setLoadError(err.message || 'Could not load products.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [productsProp]);

  const products = productsProp || fetchedProducts;

  const visible = useMemo(() => {
    const inStock = products.filter((p) => p.stock);
    return filter === 'All' ? inStock : inStock.filter((p) => p.cat === filter);
  }, [products, filter]);

  return (
    <section id="shop" className="mx-auto max-w-6xl px-4 py-20 sm:px-8">
      <div className="mb-10 text-center">
        <div className="mb-3 text-xs font-extrabold uppercase tracking-[0.2em] text-accent">
          The catalog
        </div>
        <h2 className="text-[clamp(1.8rem,4vw,2.5rem)] font-black tracking-tight">
          Tap &quot;Order on WhatsApp&quot; on any item
        </h2>
        <p className="mt-2 text-sm text-text-muted">
          Your message goes straight to our line with the product details filled in.
        </p>
      </div>

      {loadError && <p className="mb-8 text-center text-sm font-bold text-warning">{loadError}</p>}

      <div className="mb-10 flex flex-wrap justify-center gap-2">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => setFilter(cat)}
            className={`rounded-full border px-4 py-1.5 text-sm font-bold transition-colors ${
              filter === cat
                ? 'border-accent bg-accent text-black'
                : 'border-white/10 text-text-muted hover:border-white/20 hover:text-text'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 text-center text-text-muted">Loading products…</div>
      ) : visible.length ? (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {visible.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      ) : (
        <div className="py-16 text-center text-text-muted">No products in this category yet.</div>
      )}
    </section>
  );
}
